import React, { useState } from "react";
import { TextField, IconButton, Box, Tooltip, Typography } from "@mui/material";
import { Visibility, VisibilityOff, Autorenew, Close, Lock, Shield, Security } from "@mui/icons-material";
import { Field, useFormState, useForm } from "react-final-form";
import zxcvbn from "zxcvbn";
import { Util } from "../../../lib/util/Util";

// Configuration object to hold all literals
const config = {
  fieldName: "password",
  label: "Enter Password",
  tooltip: {
    passwordStrength: "Password strength",
    generatePassword: "Generate Password",
    clearPassword: "Clear Password",
  },
  inputType: {
    text: "text",
    password: "password",
  },
  variant: "outlined",
  margin: "normal",
};

const styles = {
  box: { width: "100%" },
  field: {
    backgroundColor: "#FFFFFF", // Input background
    borderRadius: "4px", // Input border radius
    pr: "10px", // Adjust padding on the right
  },
};

export const getConfig = () => {
  return Util.JSON_COPY(config);
};
export const getStyles = () => {
  return Util.JSON_COPY(styles);
};

const CustomPasswordWizField = ({ config = config, styles = styles, handleError }) => {
  const { values } = useFormState();
  const form = useForm();
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Generate a Random Password
  const generatePassword = () => {
    const length = 12;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@$!%*?&";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    form.change("password", password);
  };

  // Evaluate Password Strength using zxcvbn
  const evaluatePasswordStrength = (password) => {
    const result = zxcvbn(password);
    const score = result.score; // Score ranges from 0 (weak) to 4 (very strong)
    const feedback = result.feedback.suggestions.join(" ") || "Good password.";

    let strengthInfo;

    // Mapping score to strength label, color, and icon
    switch (score) {
      case 0:
        strengthInfo = {
          label: "Weak",
          color: "red",
          icon: <Lock fontSize="small" />
        };
        break;
      case 1:
        strengthInfo = {
          label: "Weak",
          color: "orange",
          icon: <Lock fontSize="small" />
        };
        break;
      case 2:
        strengthInfo = {
          label: "Fair",
          color: "yellow",
          icon: <Shield fontSize="small" />
        };
        break;
      case 3:
        strengthInfo = {
          label: "Strong",
          color: "green",
          icon: <Security fontSize="small" />
        };
        break;
      case 4:
        strengthInfo = {
          label: "Very Strong",
          color: "darkgreen",
          icon: <Security fontSize="small" />
        };
        break;
      default:
        strengthInfo = {
          label: "Unknown",
          color: "gray",
          icon: <Lock fontSize="small" />
        };
    }

    return { strengthInfo, feedback };
  };

  const { strengthInfo, feedback } = evaluatePasswordStrength(values?.password || "");

  return (
    <Box sx={styles.box}>
      <Field
        name={config.fieldName}
        subscription={{ value: true, pristine: true, error: true, touched: true }}
      >
        {({ input, meta }) => {
          if (handleError) {
            handleError(!!meta.error);
          }
          return (
            <>
              {/* Password Controls above Input */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0 }}>
                <Tooltip title={config.tooltip.passwordStrength}>
                  <IconButton edge="start" size="small" sx={{ color: strengthInfo.color }}>
                    {strengthInfo.icon}
                  </IconButton>
                </Tooltip>

                {/* Toggle Password Visibility */}
                <IconButton edge="start" size="small" onClick={togglePasswordVisibility}>
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>

                {/* Generate Password */}
                <Tooltip title={config.tooltip.generatePassword}>
                  <IconButton edge="start" size="small" onClick={generatePassword}>
                    <Autorenew fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Clear Password */}
                <Tooltip title={config.tooltip.clearPassword}>
                  <IconButton edge="start" size="small" onClick={() => form.change(config.fieldName, "")}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Password Field with no top margin or padding */}
              <TextField
                {...input}
                label={config.label}
                fullWidth
                margin={config.margin}
                error={!!meta.error && meta.touched}
                helperText={meta.touched && meta.error}
                type={showPassword ? config.inputType.text : config.inputType.password}
                variant={config.variant}
                InputProps={{
                  style: { ...styles.field },
                }}
                sx={{ marginTop: 0, paddingTop: 0 }} // No margin or padding on top
              />

              {/* Password Strength Feedback */}
              <Typography variant="caption" color={strengthInfo.color} sx={{ mt: 1 }}>
                {feedback}
              </Typography>
            </>
          );
        }}
      </Field>
    </Box>
  );
};

export default CustomPasswordWizField;
